/**
 * GrowthChart Component
 *
 * Displays baby growth data plotted against WHO percentile curves.
 * Uses Recharts for visualization.
 */

import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export interface GrowthMeasurement {
  ageMonths: number;
  value: number;
  date: string;
  percentile?: number;
  originalValue?: number;
  originalUnit?: 'kg' | 'lbs';
}

export interface PercentileCurve {
  percentile: number;
  data: Array<{ age: number; value: number }>;
}

interface GrowthChartProps {
  measurements: GrowthMeasurement[];
  curves: PercentileCurve[];
  metric: 'weight' | 'height' | 'headCircumference';
  maxAge?: number;
  className?: string;
  babyAgeMonths?: number;
}

const metricLabels = {
  weight: { label: 'Weight', unit: 'kg' },
  height: { label: 'Height', unit: 'cm' },
  headCircumference: { label: 'Head Circumference', unit: 'cm' },
};

const percentileColors: Record<number, string> = {
  3: '#ef4444', // red
  15: '#f97316', // orange
  50: '#22c55e', // green
  85: '#f97316', // orange
  97: '#ef4444', // red
};

const percentileStyles: Record<number, string> = {
  3: '4 2', // dashed
  15: '8 4', // dashed
  50: '0', // solid
  85: '8 4', // dashed
  97: '4 2', // dashed
};

export default function GrowthChart({
  measurements,
  curves,
  metric,
  maxAge = 24,
  className = '',
  babyAgeMonths,
}: GrowthChartProps) {
  const { label, unit } = metricLabels[metric];

  // Auto-detect weeks mode for babies under 4 months
  const useWeeks = babyAgeMonths !== undefined && babyAgeMonths < 4;
  const MONTHS_TO_WEEKS = 365.25 / 12 / 7; // ~4.348

  // Combine percentile curves and measurements into chart data
  const chartData = useMemo(() => {
    const data: Array<Record<string, number | undefined>> = [];

    if (useWeeks) {
      // Weeks mode: convert month-based curve data to weeks
      const maxWeeks = 17; // ~4 months
      // Generate weekly ticks from 0 to maxWeeks
      for (let week = 0; week <= maxWeeks; week++) {
        const point: Record<string, number | undefined> = { age: week };
        data.push(point);
      }

      // Interpolate curve values at each week
      curves.forEach((curve) => {
        const curveDataWeeks = curve.data
          .filter((d) => d.age * MONTHS_TO_WEEKS <= maxWeeks + 1)
          .map((d) => ({ week: d.age * MONTHS_TO_WEEKS, value: d.value }))
          .sort((a, b) => a.week - b.week);

        for (const point of data) {
          const week = point.age as number;
          // Find surrounding data points for interpolation
          let lower = curveDataWeeks[0];
          let upper = curveDataWeeks[curveDataWeeks.length - 1];
          for (let i = 0; i < curveDataWeeks.length - 1; i++) {
            if (curveDataWeeks[i].week <= week && curveDataWeeks[i + 1].week >= week) {
              lower = curveDataWeeks[i];
              upper = curveDataWeeks[i + 1];
              break;
            }
          }
          if (lower && upper && upper.week !== lower.week) {
            const ratio = (week - lower.week) / (upper.week - lower.week);
            point[`p${curve.percentile}`] = lower.value + ratio * (upper.value - lower.value);
          } else if (lower) {
            point[`p${curve.percentile}`] = lower.value;
          }
        }
      });

      // Add measurement points (convert ageMonths to weeks)
      measurements.forEach((m) => {
        const ageWeeks = m.ageMonths * MONTHS_TO_WEEKS;
        if (ageWeeks > maxWeeks) return;
        const existingPoint = data.find(
          (d) => Math.round(d.age as number) === Math.round(ageWeeks)
        );
        if (existingPoint) {
          existingPoint.measurement = m.value;
        } else {
          const newPoint: Record<string, number | undefined> = {
            age: ageWeeks,
            measurement: m.value,
          };
          data.push(newPoint);
        }
      });
    } else {
      // Months mode: original behavior
      for (let age = 0; age <= maxAge; age++) {
        const point: Record<string, number | undefined> = { age };
        curves.forEach((curve) => {
          const curvePoint = curve.data.find((d) => d.age === age);
          if (curvePoint) {
            point[`p${curve.percentile}`] = curvePoint.value;
          }
        });
        data.push(point);
      }

      measurements.forEach((m) => {
        const existingPoint = data.find(
          (d) => Math.round(d.age as number) === Math.round(m.ageMonths)
        );
        if (existingPoint) {
          existingPoint.measurement = m.value;
        } else {
          const newPoint: Record<string, number | undefined> = {
            age: m.ageMonths,
            measurement: m.value,
          };
          data.push(newPoint);
        }
      });
    }

    // Sort by age
    return data.sort((a, b) => (a.age as number) - (b.age as number));
  }, [measurements, curves, maxAge, useWeeks, MONTHS_TO_WEEKS]);

  const ageUnit = useWeeks ? 'weeks' : 'months';

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label: age }: any) => {
    if (active && payload && payload.length) {
      const measurementPayload = payload.find((p: any) => p.dataKey === 'measurement');

      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">
            Age: {Math.round(age)} {ageUnit}
          </p>
          {measurementPayload && (
            <p className="text-primary-600 dark:text-primary-400 font-medium">
              {label}: {measurementPayload.value.toFixed(2)} {unit}
            </p>
          )}
          {payload
            .filter((p: any) => p.dataKey !== 'measurement')
            .map((p: any) => (
              <p key={p.dataKey} className="text-sm text-gray-500 dark:text-gray-400">
                {p.dataKey.replace('p', '')}th: {p.value?.toFixed(2)} {unit}
              </p>
            ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {label} Growth Chart
      </h3>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis
              dataKey="age"
              label={{ value: `Age (${ageUnit})`, position: 'bottom', offset: -5 }}
              className="text-gray-600 dark:text-gray-400"
              ticks={useWeeks ? [0, 2, 4, 6, 8, 10, 12, 14, 16] : undefined}
            />
            <YAxis
              label={{ value: `${label} (${unit})`, angle: -90, position: 'insideLeft' }}
              className="text-gray-600 dark:text-gray-400"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* Percentile curves */}
            {curves.map((curve) => (
              <Line
                key={`p${curve.percentile}`}
                type="monotone"
                dataKey={`p${curve.percentile}`}
                name={`${curve.percentile}th percentile`}
                stroke={percentileColors[curve.percentile] || '#9ca3af'}
                strokeWidth={curve.percentile === 50 ? 2 : 1}
                strokeDasharray={percentileStyles[curve.percentile] || '0'}
                dot={false}
                connectNulls
              />
            ))}

            {/* Baby's measurements */}
            <Line
              type="monotone"
              dataKey="measurement"
              name={`Baby's ${label}`}
              stroke="#3b82f6"
              strokeWidth={3}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
              activeDot={{ r: 8 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend explanation */}
      <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        <p>
          Percentile curves show how your baby compares to WHO growth standards.
          The 50th percentile (green line) represents the median.
        </p>
        <div className="flex flex-wrap gap-4 mt-2">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            3rd/97th (unusual)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            15th/85th (monitor)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            50th (median)
          </span>
        </div>
      </div>
    </div>
  );
}
